import { Container, styled } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import LoadingMask from '../../../components/LoadingMask';
import { useFetchData } from '../../../hooks/useFetchData';
import { selectFolders } from '../../../redux/reducers/folderReducer';
import { createRun, getAllProjectRuns, updateRunQuestions } from '../../../services/dealGPT';
import type { Project, ProjectRun, ProjectWithLastScanned } from '../../../Types/dealGPT';
import { DealGPTView, ProjectRunStatus, ProjectStatus } from '../../../Types/enums';
import { projectPathUrl } from '../../../util/routesUrls';
import Chat from '../../Chat/Chat';
import DealGPTContainer from '../../DealGPT/ViewModels/DealGPTContainer';
import { useAuth } from '../../Providers/AuthProvider';
import { NavigationIndexProvider } from '../../Providers/NavigationIndexProvider';
import { setPageTitle } from '../utils';
import Analysis from './Analysis';
import DataRequestList from './DataRequestList';
import Discovery from './Discovery';
import ScopeAreas from './ScopeAreas';
import SemaProcessing from './SemaProcessing';

const ProjectContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  height: '100%',
});

const chatContainerStyles = {
  maxWidth: '100%',
  paddingLeft: '0',
  paddingRight: '0',
  margin: '0',
};

export type DealGPTProjectProps = { selectedView: DealGPTView; projectId: Project['id'] };

const DealGPTProject = ({ selectedView, projectId }: DealGPTProjectProps): JSX.Element => {
  const navigate = useNavigate();
  const [view, setView] = useState<DealGPTView>();
  const [projectRuns, setProjectRuns] = useState<ProjectRun[]>();
  const [selectedRun, setSelectedRun] = useState<ProjectRun>();

  const location = useLocation();
  const { isDealGPTAdmin } = useAuth();
  const { currentFolder } = useSelector(selectFolders);

  const {
    data: project,
    refetch: refetchProject,
    startPolling,
    stopPolling,
  } = useFetchData<ProjectWithLastScanned>({
    urlPath: projectPathUrl(projectId, isDealGPTAdmin),
  });

  const onQuestionsPreferencesChanged = useCallback(
    async (newRun: boolean, collections: string[]) => {
      try {
        if (newRun) {
          enqueueSnackbar('Creating Run and inserting Questions', {
            variant: 'info',
          });

          const newRun = await createRun(projectId, { collections });
          setSelectedRun(newRun);
        } else {
          if (!selectedRun) return;

          enqueueSnackbar('Inserting Questions', {
            variant: 'info',
          });

          await updateRunQuestions(projectId, selectedRun?.id, { collections });
        }

        refetchProject();
      } catch (error) {
        enqueueSnackbar(
          `Failed ${newRun ? 'creating a new run' : 'inserting questions'}: ${error}`,
          { variant: 'error' }
        );
      }
    },
    [projectId, selectedRun, refetchProject]
  );

  // Redirect to Home Page if the project is closed
  useEffect(() => {
    if (project?.status === ProjectStatus.CLOSED) {
      enqueueSnackbar(`Project ${project.name} has been closed and can no longer be accessed`, {
        variant: 'error',
      });
      navigate('/intellio-advantage');
    }
  }, [project, navigate]);

  // Update Page title with current project name
  useEffect(
    () => setPageTitle({ projectId: project?.id, projectName: project?.name }),
    [project, selectedView]
  );

  // Fetch project every 10 seconds if project is inserting or processing questions
  useEffect(() => {
    if (
      project?.projectRunStatus === ProjectRunStatus.PROCESSING_QUESTIONS ||
      project?.projectRunStatus === ProjectRunStatus.INSERTING_QUESTIONS
    ) {
      startPolling(10000);
    }

    return () => {
      stopPolling();
    };
  }, [project, startPolling, stopPolling]);

  useEffect(() => {
    if (location.pathname.includes('analysis') || location.pathname.includes('scope-areas')) {
      setView(DealGPTView.ANALYSIS);
    } else if (location.pathname.includes('chat')) {
      setView(DealGPTView.CHAT);
    } else if (location.pathname.includes('discovery')) {
      setView(DealGPTView.DISCOVERY);
    } else if (location.pathname.includes('data')) {
      setView(DealGPTView.DATA_PROCESSING);
    }
  }, [location, view, setView]);

  useEffect(() => {
    if (!project) return;

    const handleGetProjectRuns = async () => {
      try {
        const newRuns = await getAllProjectRuns(project.id, 'DISCOVERY');

        if (newRuns.length > 0) {
          const runData = newRuns.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          setProjectRuns(runData ?? []);
        }
      } catch {
        enqueueSnackbar('Failed to load project runs.', { variant: 'error' });
      }
    };

    // Retrieves project runs for the following cases:
    // 1. Project runs have not yet been loaded in
    // 2. A scan is in progress and current number of project runs is equal to 0
    // 3. A scan is in progress and the most recent project run does not match the projects latest run id

    if (projectRuns === undefined) {
      handleGetProjectRuns();
    } else if (
      projectRuns.length == 0 &&
      project.projectRunStatus === ProjectRunStatus.PROCESSING_QUESTIONS
    ) {
      handleGetProjectRuns();
    } else if (
      projectRuns.length > 0 &&
      !projectRuns[0].endedAt &&
      project.projectRunStatus === ProjectRunStatus.COMPLETED_SUCCESSFULLY
    ) {
      handleGetProjectRuns();
    } else if (projectRuns.length > 0 && project.lastDiscoveryRunId) {
      if (!projectRuns.find((run) => run.id === project.lastDiscoveryRunId)) {
        handleGetProjectRuns();
      }
    }
  }, [project, projectRuns, selectedRun]);

  if (!project) {
    return (
      <DealGPTContainer>
        <LoadingMask isLoading={true} />
      </DealGPTContainer>
    );
  }

  return (
    <DealGPTContainer>
      <div
        style={{
          overflow: 'auto',
          width: '100%',
          height: '100%',
          maxHeight: 'calc(100vh - var(--banner-height, 0px))',
        }}
      >
        <Container
          style={selectedView == DealGPTView.CHAT ? chatContainerStyles : {}}
          sx={{ paddingTop: '10px', height: '100%' }}
        >
          <ProjectContainer
            sx={{
              // using fit-content here means the loading mask will scale to cover the overflow contents
              height:
                selectedView === DealGPTView.CHAT || selectedView === DealGPTView.DATA_REQUEST_LIST
                  ? '95%'
                  : 'fit-content',
              // including a minHeight breaks this so the loading mask won't cover the overflow contents.
              // However, the chat page relies on the parent component taking up the full height, so this
              // ensures that on the chat page, the spacing still works well. The added note to the user
              // about needing to do the index requires the conditional difference in the minHeight
              minHeight:
                selectedView !== DealGPTView.CHAT
                  ? undefined
                  : !currentFolder?.indexIds?.length
                  ? 'calc(100% - 100px)'
                  : 'calc(100% - 60px)',
            }}
          >
            {selectedView === DealGPTView.DISCOVERY && (
              <Discovery
                project={project}
                refetchProject={refetchProject}
                onQuestionsPreferencesChanged={onQuestionsPreferencesChanged}
                projectRuns={projectRuns}
                setSelectedRun={setSelectedRun}
                selectedRun={selectedRun}
              />
            )}

            {selectedView === DealGPTView.CHAT && <Chat project={project} />}
            {selectedView === DealGPTView.DATA_PROCESSING && <SemaProcessing project={project} />}
            {selectedView === DealGPTView.DATA_REQUEST_LIST && (
              <DataRequestList project={project} refetchProject={refetchProject} />
            )}
            <NavigationIndexProvider>
              {selectedView === DealGPTView.SCOPE_AREAS && <ScopeAreas project={project} />}
              {selectedView === DealGPTView.ANALYSIS && <Analysis project={project} />}
            </NavigationIndexProvider>
          </ProjectContainer>
        </Container>
      </div>
    </DealGPTContainer>
  );
};

export default DealGPTProject;
